import { useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import { getBondGia } from "@/lib/brokerGia";
import type { BondSubmission } from "@/lib/brokerBonds";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import { statusBadgeClass } from "@/lib/listFormat";
import {
  isPlausibleEmail,
  offersSpreadLetterSend,
  readSpreadLetter,
  sendSpreadLetter,
  spreadLetterRecipients,
  spreadLetterStatusLabel,
  type SpreadLetterRecipient,
  type SpreadLetterFigures,
  type SpreadLetterStatus,
  type SpreadLetterView,
} from "@/lib/spreadLetter";
import { SelectField } from "@/components/SelectField";
import { TextField } from "@/components/TextField";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S331 — "Request letter from client": the Bid Spread Letter's status chip,
// the send (confirmed first), and the executed letter's download once signed.
// BRK-S421: the confirm picks WHO signs — an indemnitor with an email, from the
// order's GIA signers, or "Someone else" typed in by name and email — and the
// send carries that person's name, so the envelope names them (PMS-S843). It
// opens on manual entry when no indemnitor has an email or the GIA read fails;
// the account's own email is never prefilled (it is the company's inbox, not a
// person who signs). Rendered on the won-bid
// screen (inside the spread-letter notice, beside the offline upload slot);
// the submission profile no longer carries it (BRK-S502).
//
// NOTHING OPTIMISTIC (NN #7): "Letter sent to {email} on {date}" renders only
// from the send's 2xx, and the chip moves only to what that response says. A
// refusal (already sent, no template on file, not required) is PMS's sentence,
// inline. NEVER A GATE: nothing here disables anything else on the page.

function chipClass(status: SpreadLetterStatus | null): string {
  switch (status) {
    case "signed":
    case "uploaded":
      return statusBadgeClass("approved");
    case "sent":
      return statusBadgeClass("submitted");
    default:
      return statusBadgeClass("draft");
  }
}

const LETTER_FALLBACK = "bid-spread-letter.pdf";

/** The Signer select's manual-entry choice. Never a GIA roster id (UUIDs). */
const SOMEONE_ELSE = "someone-else";

export interface SpreadLetterRequestProps {
  orderId: string;
  accountId: string;
  /** The order view's `spreadLetter` block, raw (read here). */
  spreadLetter: unknown;
  /** The won-bid screen's figures (PMS uses them when no won result is
   *  saved yet). Plain numbers. */
  figures?: SpreadLetterFigures;
  /**
   * Set while the screen's figures differ from the RECORDED won result, which
   * is what PMS fills the letter from: the button reads "Save and send
   * letter" and this runs first. It resolves false when the save failed (the
   * page renders why), and then nothing is sent.
   */
  saveFirst?: () => Promise<boolean>;
  onOrderChanged?: (order: BondSubmission) => void;
}

export function SpreadLetterRequest({
  orderId,
  accountId,
  spreadLetter,
  figures = {},
  saveFirst,
  onOrderChanged,
}: SpreadLetterRequestProps) {
  const { authedRequest, authedBlob } = useAuth();
  const [view, setView] = useState<SpreadLetterView | null>(() =>
    readSpreadLetter(spreadLetter),
  );
  const [confirming, setConfirming] = useState(false);
  // The indemnitors to pick from; null while the GIA is read.
  const [recipients, setRecipients] = useState<SpreadLetterRecipient[] | null>(
    null,
  );
  const [choice, setChoice] = useState<string>(SOMEONE_ELSE);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"saving" | "sending" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function openConfirm() {
    setError(null);
    setConfirming(true);
    if (recipients !== null) return;
    let found: SpreadLetterRecipient[] = [];
    try {
      found = spreadLetterRecipients(
        (await getBondGia(authedRequest, orderId)).signers,
      );
    } catch {
      // Typing the person in still sends; a failed read is not a failure to send.
    }
    setRecipients(found);
    setChoice(found[0]?.id ?? SOMEONE_ELSE);
  }

  const picked =
    choice === SOMEONE_ELSE
      ? null
      : (recipients?.find((r) => r.id === choice) ?? null);

  async function send() {
    const signer = picked ?? { name: name.trim(), email: email.trim() };
    const nameProblem = signer.name ? null : "Enter the signer’s name.";
    const emailProblem = isPlausibleEmail(signer.email)
      ? null
      : "Enter the signer’s email address.";
    setNameError(nameProblem);
    setEmailError(emailProblem);
    if (nameProblem || emailProblem) return;
    setError(null);
    if (saveFirst) {
      setBusy("saving");
      // NOTHING SENT ON A FAILED SAVE: the letter would carry figures the
      // won bid does not.
      const saved = await saveFirst();
      if (!saved) {
        setBusy(null);
        return;
      }
    }
    setBusy("sending");
    try {
      const fresh = await sendSpreadLetter(authedRequest, orderId, {
        email: signer.email,
        name: signer.name,
        ...(figures.finalContractPrice !== undefined
          ? { finalContractPrice: figures.finalContractPrice }
          : {}),
        ...(figures.nextClosestBidAmount !== undefined
          ? { nextClosestBidAmount: figures.nextClosestBidAmount }
          : {}),
      });
      setView(readSpreadLetter(fresh.spreadLetter));
      setSentTo(signer.email);
      setConfirming(false);
      onOrderChanged?.(fresh);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(null);
    }
  }

  async function download(documentId: string) {
    setDownloading(true);
    setError(null);
    try {
      saveFile(
        await downloadAccountDocument(authedBlob, accountId, documentId),
        LETTER_FALLBACK,
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setDownloading(false);
    }
  }

  const offers = offersSpreadLetterSend(view) && sentTo === null;

  return (
    <div
      data-testid="spread-letter-request"
      className="flex min-w-0 flex-col gap-3"
    >
      {view && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-brk-text">
          Letter from client:
          <span
            data-testid="spread-letter-status"
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${chipClass(view.status)}`}
          >
            {spreadLetterStatusLabel(view.status)}
          </span>
          {view.status === "sent" && view.sentAt && sentTo === null && (
            <span className="text-brk-dim">
              Sent {formatBondDate(view.sentAt)}
            </span>
          )}
          {view.status === "signed" && view.signedAt && (
            <span className="text-brk-dim">
              Signed {formatBondDate(view.signedAt)}
            </span>
          )}
        </p>
      )}

      {sentTo !== null && (
        <p role="status" className="break-words text-sm text-brk-text">
          Letter sent to {sentTo}
          {view?.sentAt ? ` on ${formatBondDate(view.sentAt)}` : ""}.
        </p>
      )}

      {view?.status === "signed" && view.documentId && (
        <button
          type="button"
          onClick={() => void download(view.documentId as string)}
          disabled={downloading}
          className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {downloading ? "Preparing…" : "Download the signed letter"}
        </button>
      )}

      {offers && !confirming && (
        <button
          type="button"
          onClick={() => void openConfirm()}
          className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
        >
          Request letter from client
        </button>
      )}

      {offers && confirming && (
        <div
          role="group"
          aria-label="Send the Bid Spread Letter"
          className="flex flex-col gap-3 rounded-md border border-brk-border bg-brk-panel p-3"
        >
          <p className="text-sm text-brk-text">
            We’ll send the Bid Spread Letter to your client for signature.
            Choose who signs it:
          </p>
          {recipients === null ? (
            <p className="text-sm text-brk-dim">Finding the indemnitors…</p>
          ) : (
            <>
              <div className="w-full md:w-96">
                <SelectField
                  label="Signer"
                  value={choice}
                  onChange={(e) => {
                    setChoice(e.target.value);
                    setNameError(null);
                    setEmailError(null);
                  }}
                  options={[
                    ...recipients.map((r) => ({
                      value: r.id,
                      label: `${r.name} — ${r.email}`,
                    })),
                    { value: SOMEONE_ELSE, label: "Someone else" },
                  ]}
                  required
                />
              </div>
              {picked === null && (
                <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2">
                  <TextField
                    label="Signer’s name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setNameError(null);
                    }}
                    error={nameError ?? undefined}
                    required
                  />
                  <TextField
                    label="Signer’s email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError(null);
                    }}
                    error={emailError ?? undefined}
                    required
                  />
                </div>
              )}
            </>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void send()}
              disabled={busy !== null || recipients === null}
              className={`${buttonClass({ size: "sm" })} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {busy === "saving"
                ? "Saving…"
                : busy === "sending"
                  ? "Sending…"
                  : saveFirst
                    ? "Save and send letter"
                    : "Send letter"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                setError(null);
              }}
              disabled={busy !== null}
              className="text-sm font-medium text-brk-accent hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
    </div>
  );
}
